import { QueryClient, QueryKey } from "@tanstack/react-query";

import { applyUpdatedWithValueDelta } from "lib/utils";
import {
  LeadQuery,
  LeadsQuery,
  LeadsSavedToEntitiesQuery,
} from "../../../../generated/app-service-gql/graphql";
import { LeadEntityType as EntityType } from "../../../../types/graphQLEnums";
import { LeadDetails } from "../useLead";
import { LeadFromSearch } from "../useLeads";
import { updateLeadEntityQuery } from "./updateLeadEntityQuery";

// The entity types the lead list query shows on each lead
const LEAD_LIST_ENTITY_TYPES = [
  EntityType.Buyer,
  EntityType.Supplier,
  EntityType.Organisation,
  EntityType.CustomOrganisation,
  EntityType.Framework,
];

type LeadEntityResponse = {
  entityId: string;
  entityType: EntityType;
  leadId: string;
  leadName: string;
};

/**
 * Function to help update the cached entities when adding/removing entities to/from a lead
 */
export async function updateLeadEntityCache({
  queryClient,
  entities,
  isAdding,
}: {
  queryClient: QueryClient;
  entities: LeadEntityResponse[];
  isAdding: boolean;
}) {
  const allLeadsQueries = queryClient.getQueryCache().findAll({ queryKey: ["leads"] });

  for (const entity of entities) {
    const { entityId, entityType, leadId, leadName } = entity;

    // Update saved to entity cache for this entity ID
    await updateCachedLeadsSavedToEntities({
      queryClient,
      entityId,
      leadId,
      isAdding,
    });

    // Updates the relevant query caches of whichever entity type is being updated
    // e.g notice, contact, document, framework etc
    updateLeadEntityQuery({
      queryClient,
      entityId,
      entityType,
      leadName,
      leadId,
      isAdding,
    });

    // ADDING ENTITY LOGIC
    if (isAdding) {
      // Since backend does not return full updated entity info,
      // the safest is to invalidate the specific lead cache.
      await queryClient.invalidateQueries({ queryKey: ["lead", leadId] });

      // We don't currently need to update any other entity types when adding entities,
      // because only buyers and supplier, framework entities are currently on the lead list query results.
      if (!LEAD_LIST_ENTITY_TYPES.includes(entityType)) {
        continue;
      }

      // Add buyer/supplier/framework to lead list queries
      for (const { queryKey } of allLeadsQueries) {
        const data = queryClient.getQueryData<LeadsQuery>(queryKey);
        if (!data?.leads) continue;
        const queryIncludesLead = data.leads.results.some((l) => l.id === leadId);

        // If query includes lead that had an entity added to it, invalidate the lead list query cache to get updated buyers/suppliers/frameworks
        if (queryIncludesLead) {
          await queryClient.invalidateQueries({ queryKey });
        }
      }

      // Add lead to entity it was saved to
      // REMOVING ENTITY LOGIC
    } else {
      const entityRemover = leadDetailEntityRemover(entityId)[entityType];
      if (!entityRemover) {
        throw new Error(`Unhandled lead entity type for cache update: ${entityType}`);
      }

      // Update the lead detail page cache
      updateLeadQuery(queryClient, leadId, entityRemover);

      // Only need to update lead list query caches if the entity type is a buyer, supplier, custom buyer or framework
      if (!LEAD_LIST_ENTITY_TYPES.includes(entityType)) {
        continue;
      }

      for (const { queryKey } of allLeadsQueries) {
        const data = queryClient.getQueryData<LeadsQuery>(queryKey);
        if (!data?.leads) continue;

        const queryIncludesLead = data.leads.results.some((l) => l.id === leadId);
        // If query includes lead that had an entity removed from it, update the lead to remove the buyer/supplier/framework/custom buyer
        if (queryIncludesLead) {
          updateSearchLeadsQuery(queryClient, queryKey, [leadId], (lead) => ({
            ...lead,
            buyers: lead.buyers.filter((b) => b.id !== entityId),
            customBuyers: lead.customBuyers.filter((b) => b.id !== entityId),
            suppliers: lead.suppliers.filter((s) => s.id !== entityId),
            frameworks: lead.frameworks.filter((f) => f.id !== entityId),
          }));
        }
      }
    }
  }
}

/**
 * Contains all of the cache update actions to remove each type of entity from a lead detail object
 * @param entityId
 * @returns
 */
function leadDetailEntityRemover(
  entityId: string,
): Partial<Record<EntityType, (lead: LeadDetails) => LeadDetails>> {
  return {
    [EntityType.Contact]: (lead) => ({
      ...lead,
      contacts: lead.contacts.filter((c) => c.id !== entityId),
    }),
    [EntityType.ContactCognism]: (lead) => ({
      ...lead,
      contacts: lead.contacts.filter((c) => c.id !== entityId),
    }),
    [EntityType.Document]: (lead) => ({
      ...lead,
      documents: lead.documents.filter((d) => d.id !== entityId),
    }),
    [EntityType.Notice]: (lead) => ({
      ...lead,
      notices: lead.notices.filter((n) => n.procurementStage.id !== entityId),
    }),
    [EntityType.Framework]: (lead) => ({
      ...lead,
      frameworks: lead.frameworks.filter((f) => f.id !== entityId),
    }),
    [EntityType.Buyer]: (lead) => ({
      ...lead,
      buyers: lead.buyers.filter((b) => b.id !== entityId),
    }),
    [EntityType.Supplier]: (lead) => ({
      ...lead,
      suppliers: lead.suppliers.filter((s) => s.id !== entityId),
    }),
    [EntityType.Organisation]: (lead) => ({
      ...lead,
      buyers: lead.buyers.filter((b) => b.id !== entityId),
      suppliers: lead.suppliers.filter((s) => s.id !== entityId),
    }),
    [EntityType.CustomOrganisation]: (lead) => ({
      ...lead,
      customBuyers: lead.customBuyers.filter((b) => b.id !== entityId),
    }),
  };
}

/**
 * Updates the lead query cache for a given lead ID
 * @param queryClient
 * @param leadId
 * @param updater
 */
export function updateLeadQuery(
  queryClient: QueryClient,
  leadId: string,
  updater: (lead: LeadQuery["lead"]) => LeadQuery["lead"],
) {
  const key = ["lead", leadId];
  queryClient.setQueryData<LeadQuery>(key, (prev) => {
    if (!prev) return prev;
    return { lead: updater(prev.lead) };
  });
}

/**
 * Updates the useLeads query cache for given lead IDs
 * @param queryClient
 * @param queryKey
 * @param leadIds
 * @param updater
 */
export function updateSearchLeadsQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  leadIds: string[],
  updater: (lead: LeadFromSearch) => LeadFromSearch,
  valueDeltaOverride?: number,
) {
  queryClient.setQueryData<LeadsQuery>(queryKey, (prev) => {
    if (!prev?.leads) return prev;
    const { results, valueDelta } = applyUpdatedWithValueDelta(
      prev.leads.results,
      leadIds,
      updater,
    );

    return {
      leads: {
        ...prev.leads,
        results,
        valueSum: (prev.leads.valueSum ?? 0) + (valueDeltaOverride ?? valueDelta),
      },
    };
  });
}

/**
 * Updates cached leadsSavedToEntities queries when entities are added/removed from a lead.
 *
 * leadsSavedToEntities uses intersection semantics: a lead appears only if saved to ALL
 * entityIds in the query. This means:
 * - Removals: Safe to optimistically remove — if a lead is removed from entity A,
 *   it can't be in the intersection of any query that includes A.
 * - Additions: Cannot optimistically add — saving to entity A doesn't mean the lead
 *   is saved to all other entities in a query like [A, B, C]. Must invalidate instead.
 */
async function updateCachedLeadsSavedToEntities({
  queryClient,
  entityId,
  leadId,
  isAdding,
}: {
  queryClient: QueryClient;
  entityId: string;
  leadId: string;
  isAdding: boolean;
}) {
  const matchingQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["leadsSavedToEntities"] });

  for (const query of matchingQueries) {
    const entityIds = query.queryKey[1];
    if (!Array.isArray(entityIds) || !entityIds.includes(entityId)) {
      continue;
    }

    if (isAdding) {
      await queryClient.invalidateQueries({ queryKey: query.queryKey });
    } else {
      queryClient.setQueryData<LeadsSavedToEntitiesQuery>(query.queryKey, (oldData) => {
        if (!oldData) return { leadsSavedToEntities: [] };
        return {
          leadsSavedToEntities: oldData.leadsSavedToEntities.filter((l) => l.id !== leadId),
        };
      });
    }
  }
}
